import React, { ReactElement, useEffect, useState } from 'react';
import { Button, Modal, notification, Space } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { capitalize } from 'lodash';

import { isBreederActive } from 'dogpark/utilities/breeders';
import { testId } from 'dogpark/utilities/test/datatestId';
import { Breeder } from 'dogpark/types';
import { apiBreeder } from 'utilities/http/api/dogPark/apiBreeder';

import { BreedersColumnActiveStatusStyles } from './BreedersColumnActiveStatus.styles';

interface Props {
  breeder: Breeder;
}

function BreedersColumnActiveStatus(props: Props): ReactElement {
  const { breeder } = props;
  const [isActive, setIsActive] = useState(false);
  const statusLabel = isActive ? `active` : `inactive`;

  const handleBreederStatus = () => {
    const statusMessage = isActive ? `deactivate` : `activate`;
    const statusMessagePastSuffix = `${statusMessage}d`;

    const postBreederStatus = async () => {
      const breedersResp = isActive
        ? await apiBreeder.deactivateBreeder(breeder.id)
        : await apiBreeder.activeBreeder(breeder.id);

      if (!breedersResp.isOk) {
        const errorMessage = `Breeder id ${breeder.id} couldn't be ${statusMessagePastSuffix}`;
        notification.error({ key: 'post-breeder-notification', message: errorMessage });
        throw Error(errorMessage);
      }

      notification.success({
        key: 'post-breeder-notification',
        message: `Breeder id ${breeder.id} successfully ${statusMessagePastSuffix}`,
      });

      setIsActive((statusState) => !statusState);
    };

    Modal.confirm({
      autoFocusButton: null,
      title: <b>Do you want to {statusMessage} this Breeder?</b>,
      content: 'Please confirm to proceed.',
      onOk: postBreederStatus,
    });
  };

  useEffect(() => {
    setIsActive(isBreederActive(breeder));
  }, [breeder]);

  return (
    <BreedersColumnActiveStatusStyles data-testid={testId.breedersColumnActive}>
      <Space>
        <span
          data-testid={testId.breedersColumnActiveLabel}
          className={`status-label-${statusLabel}`}
        >
          {capitalize(statusLabel)}
        </span>
        <Button
          data-testid={testId.breedersColumnActiveButton}
          type="link"
          size="small"
          icon={<EditOutlined />}
          title="Edit"
          onClick={handleBreederStatus}
        />
      </Space>
    </BreedersColumnActiveStatusStyles>
  );
}

export default BreedersColumnActiveStatus;
